import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BadgeStatus } from "@/types/general";
import { ButtonBase, Menu, MenuItem, Box } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getOrderStatusIcon } from "@reusables/BaseOrderStatusBadge";
import { SaleOrdersFormTyping } from "../types";

interface HeaderStatusControlProps {
    initialStatus?: BadgeStatus.Base;
}

const statusChipSx = {
    minHeight: "28px",
    borderRadius: "8px",
    px: "12px",
    py: "4px",
    backgroundColor: "#F4F4F4",
    color: "#515151",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.7,
    "& svg": {
        width: "16px",
        height: "16px",
    },
};

type EditableStatus = SaleOrdersFormTyping["status"];

function getEditableStatusOptions(initialStatus?: BadgeStatus.Base): EditableStatus[] {
    if (initialStatus === BadgeStatus.Base.offer) {
        return [BadgeStatus.Base.offer, BadgeStatus.Base.new];
    }

    return [];
}

export default function HeaderStatusControl(props: HeaderStatusControlProps) {
    const { initialStatus } = props;
    const { t: statusesT } = useTranslation("", { keyPrefix: "general.orders.statuses.base" });
    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();

    const [mountElement, setMountElement] = useState<HTMLElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const status = useWatch({
        control,
        name: "status",
    });

    const editableStatusOptions = React.useMemo(
        () => getEditableStatusOptions(initialStatus),
        [initialStatus]
    );
    const isStatusEditable = editableStatusOptions.length > 1;

    useEffect(() => {
        // The header mount keeps the form-owned status chip close to mutation state while
        // still letting the page header decide where that control should live visually.
        setMountElement(document.getElementById("sale_order_status_header"));
    }, []);

    if (!mountElement || !initialStatus || !status) {
        return null;
    }

    const statusChipContent = (
        <>
            {getOrderStatusIcon(status)}
            <span>{statusesT(status)}</span>
            {isStatusEditable && (
                <ChevronDown
                    size={12}
                    style={{
                        transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 120ms ease-out",
                    }}
                />
            )}
        </>
    );

    const statusChip = isStatusEditable ? (
        <ButtonBase
            type="button"
            onClick={(event) => {
                setAnchorEl(event.currentTarget);
            }}
            sx={{
                ...statusChipSx,
                transition: "background-color 150ms ease-out",
                "&:hover": {
                    backgroundColor: "#ECE9FF",
                },
            }}
        >
            {statusChipContent}
        </ButtonBase>
    ) : (
        <Box sx={statusChipSx}>{statusChipContent}</Box>
    );

    return createPortal(
        <>
            {statusChip}
            {isStatusEditable && (
                <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            mt: "10px",
                            borderRadius: "14px",
                            minWidth: "180px",
                            border: "1px solid #ECE9FF",
                            boxShadow: "0px 20px 60px rgba(60, 55, 105, 0.16)",
                            overflow: "hidden",
                            padding: "6px",
                            "& .MuiList-root": {
                                p: "6px",
                            },
                        },
                    }}
                >
                    {editableStatusOptions.map((option, index) => (
                        <MenuItem
                            key={option}
                            selected={status === option}
                            onClick={() => {
                                setValue("status", option, {
                                    shouldDirty: option !== initialStatus,
                                    shouldValidate: true,
                                });
                                setAnchorEl(null);
                            }}
                            sx={{
                                minHeight: "40px",
                                borderRadius: "10px",
                                fontSize: "14px",
                                color: "#515151",
                                gap: "8px",
                                marginBottom: `${index != editableStatusOptions.length - 1 ? "8px" : "0px"}`,
                                "&.Mui-selected": {
                                    backgroundColor: "#F4F4FF",
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: "#ECE9FF",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& svg": {
                                        width: "16px",
                                        height: "16px",
                                    },
                                }}
                            >
                                {getOrderStatusIcon(option)}
                            </Box>
                            <span>{statusesT(option)}</span>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>,
        mountElement
    );
}
